'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useId, useRef, useState } from 'react';
import {
  createComponentImplementation,
  basicCatalog,
  Button as A2Button,
  Card as A2Card,
  TextField as A2TextField,
  CheckBox as A2CheckBox,
  Slider as A2Slider,
  Tabs as A2Tabs,
  Modal as A2Modal,
} from '@a2ui/react/v0_9';
import type { ReactA2uiComponentProps, ReactComponentImplementation } from '@a2ui/react/v0_9';
import { Catalog } from '@a2ui/web_core/v0_9';
import {
  Button,
  Card,
  Input,
  Textarea,
  Checkbox,
  Slider,
  Tabs,
  Dialog,
  DialogClose,
} from '@langbridge-builder/design-system';

/**
 * LangBridge 커스텀 A2UI 카탈로그 (슬라이스 1 · 하이브리드).
 *
 * A2UI 표면을 basicCatalog(인라인스타일 프로토타입)로 그리는 대신, 우리 design-system 컴포넌트로
 * "재렌더"한다. 각 A2UI 컴포넌트의 스키마/계약(basicCatalog 의 것)은 그대로 재사용하고 render 만
 * 교체한다 → 서페이스의 데이터 바인딩·액션·검증(GenericBinder)은 손대지 않고 룩앤필만 DS 로 승격.
 *
 * 대상(=DS 원자와 1:1 대응하는 재사용 7종):
 *   Button · Card · TextField(→Input/Textarea) · CheckBox · Slider · Tabs · Modal(→Dialog)
 * 나머지(Text/Image/Row/Column/List/ChoicePicker/DateTimeInput/…)는 basicCatalog 로 폴백한다
 * (a2ui.css 의 :where 스코프 베이스라인이 스타일). 갭 컴포넌트 신규 이식은 다음 슬라이스.
 *
 * 격리: 이 파일은 `@langbridge-builder/chat-ui/a2ui` 서브패스에서만 로드된다(main 번들 무영향).
 */

type A2uiProps = ReactA2uiComponentProps<any>;

/** A2UI Button → DS Button. variant(primary/borderless/default) 매핑 + action/child/isValid. */
function LbButton({ props, buildChild }: A2uiProps) {
  const variant =
    props.variant === 'primary' ? 'primary' : props.variant === 'borderless' ? 'ghost' : 'secondary';
  return (
    <Button
      type="button"
      variant={variant}
      disabled={props.isValid === false}
      onClick={props.action}
    >
      {props.child ? buildChild(props.child) : null}
    </Button>
  );
}

/** A2UI Card → DS Card(단일 child 컨테이너). */
function LbCard({ props, buildChild }: A2uiProps) {
  return <Card>{props.child ? buildChild(props.child) : null}</Card>;
}

/** A2UI TextField → DS Input(단문/숫자/보안) 또는 Textarea(longText). label/검증 포함. */
function LbTextField({ props }: A2uiProps) {
  const id = useId();
  const isLong = props.variant === 'longText';
  const type =
    props.variant === 'number' ? 'number' : props.variant === 'obscured' ? 'password' : 'text';
  const errors: string[] = Array.isArray(props.validationErrors) ? props.validationErrors : [];
  const hasError = errors.length > 0;
  const onChange = (e: { target: { value: string } }) => props.setValue(e.target.value);
  return (
    <div className="lb-a2ui-field">
      {props.label ? (
        <label htmlFor={id} className="lb-a2ui-label">
          {props.label}
        </label>
      ) : null}
      {isLong ? (
        <Textarea id={id} invalid={hasError} value={props.value || ''} onChange={onChange} />
      ) : (
        <Input id={id} type={type} invalid={hasError} value={props.value || ''} onChange={onChange} />
      )}
      {hasError ? <span className="lb-a2ui-error">{errors[0]}</span> : null}
    </div>
  );
}

/** A2UI CheckBox → DS Checkbox(Radix). label/검증 포함. */
function LbCheckBox({ props }: A2uiProps) {
  const id = useId();
  const errors: string[] = Array.isArray(props.validationErrors) ? props.validationErrors : [];
  const hasError = errors.length > 0;
  return (
    <div className="lb-a2ui-check">
      <Checkbox
        id={id}
        label={props.label}
        checked={!!props.value}
        onCheckedChange={(c) => props.setValue(c === true)}
      />
      {hasError ? <span className="lb-a2ui-error">{errors[0]}</span> : null}
    </div>
  );
}

/** A2UI Slider → DS Slider. label 위 + 트랙 우측에 현재값. */
function LbSlider({ props }: A2uiProps) {
  const id = useId();
  const min = typeof props.min === 'number' ? props.min : 0;
  const max = typeof props.max === 'number' ? props.max : 100;
  const value = typeof props.value === 'number' ? props.value : min;
  return (
    <div className="lb-a2ui-field">
      {props.label ? (
        <label htmlFor={id} className="lb-a2ui-label">
          {props.label}
        </label>
      ) : null}
      <Slider
        value={value}
        min={min}
        max={max}
        onValueChange={(v) => props.setValue(v)}
        showValue
      />
    </div>
  );
}

/** A2UI Tabs → DS Tabs(언더라인 바). DS Tabs 는 바만 그리므로 선택 인덱스는 로컬 상태로, 활성
 * 탭의 child 는 바 아래에 buildChild 로 렌더한다. */
function LbTabs({ props, buildChild }: A2uiProps) {
  const tabs: any[] = Array.isArray(props.tabs) ? props.tabs : [];
  const [selected, setSelected] = useState('0');
  const items = tabs.map((t, i) => ({ value: String(i), label: t?.title ?? `탭 ${i + 1}` }));
  const idx = Number(selected);
  const active = tabs[idx] ?? tabs[0];
  if (!tabs.length) return null;
  return (
    <div className="lb-a2ui-tabs">
      <Tabs tabs={items} value={selected} onValueChange={setSelected} />
      <div className="lb-a2ui-tabs-content">{active?.child ? buildChild(active.child) : null}</div>
    </div>
  );
}

/** A2UI Modal → DS Dialog. trigger 클릭으로 open. DS Dialog 는 body 로 포탈되므로 그대로 두면
 * 패널의 테마 스코프([data-theme]/[data-mode] + --a2ui-*)를 벗어나 라이트로 렌더된다. 그래서
 * 포탈 컨테이너를 우리 패널([data-testid=a2ui-panel])로 지정해 Content 가 패널 하위에 들어가게 한다
 * → 테마·베이스라인·--a2ui-* 를 정상 상속(Content 는 position:fixed 라 시각적으로는 중앙 오버레이). */
function LbModal({ props, buildChild }: A2uiProps) {
  const [open, setOpen] = useState(false);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const openModal = () => {
    setContainer(
      (triggerRef.current?.closest('[data-testid="a2ui-panel"]') as HTMLElement | null) ?? null,
    );
    setOpen(true);
  };
  return (
    <>
      {/* 클릭 캐처: 내부 트리거(보통 DS Button)의 click/Enter 가 여기로 버블링돼 모달을 연다.
          span 에 role/tabIndex 를 주면 내부 button 과 중첩 인터랙티브가 되므로 주지 않는다. */}
      <span ref={triggerRef} className="lb-a2ui-modal-trigger" onClick={openModal}>
        {props.trigger ? buildChild(props.trigger) : null}
      </span>
      <Dialog open={open} onOpenChange={setOpen} container={container}>
        <div className="lb-a2ui-modal-body">
          <DialogClose className="lb-a2ui-modal-close" aria-label="닫기">
            ✕
          </DialogClose>
          {props.content ? buildChild(props.content) : null}
        </div>
      </Dialog>
    </>
  );
}

/**
 * basicCatalog 의 컴포넌트 맵을 그대로 가져와 위 7종만 DS 렌더러로 덮어쓴다. id/functions/themeSchema
 * 는 basicCatalog 것을 재사용 → catalogId 정규화·검증 함수 계약이 basicCatalog 와 동일하게 유지된다.
 */
const overrides = new Map<string, ReactComponentImplementation>([
  ['Button', createComponentImplementation(A2Button, LbButton)],
  ['Card', createComponentImplementation(A2Card, LbCard)],
  ['TextField', createComponentImplementation(A2TextField, LbTextField)],
  ['CheckBox', createComponentImplementation(A2CheckBox, LbCheckBox)],
  ['Slider', createComponentImplementation(A2Slider, LbSlider)],
  ['Tabs', createComponentImplementation(A2Tabs, LbTabs)],
  ['Modal', createComponentImplementation(A2Modal, LbModal)],
]);

const components = [...basicCatalog.components.values()].map((c) => overrides.get(c.name) ?? c);

export const langbridgeCatalog = new Catalog<ReactComponentImplementation>(
  basicCatalog.id,
  components,
  [...basicCatalog.functions.values()],
  basicCatalog.themeSchema,
);
